<template>
  <div class="row">
    <div v-if="bottle" class="col-12">
      <Heading>Nr. {{ bottle.serial }}</Heading>
      <div class="border border-light p-5">
        <div class="row">
          <div class="col-4">
            <b class="text-primary">Standort:</b><br>
            <bottle-state :item="bottle" :users="users"/>
          </div>
          <div class="col-4">
            <b class="text-primary">Flaschentyp:</b><br>
            {{ bottle.type || "-" }}
          </div>
          <div class="col-4">
            <b class="text-primary">Fällig:</b><br>
            {{ dateWithFormat(bottle.due) || "-" }}
          </div>
          <div class="col-3 offset-9 text-right">

            <button @click="edit" class="btn btn-primary btn-block mt-5 btn-sm">Bearbeiten<i
                class="fas fa-pencil-alt pl-4"></i>
            </button>
            <button @click="showModal = true" class="btn btn-primary btn-block mt-3 btn-sm">Flasche
              bewegen<i
                  class="fas fa-dolly pl-4"></i>
            </button>
          </div>
        </div>
      </div>
      <bottle-action-log
          v-if="bottle.bottle_actions && bottle.bottle_actions.length > 0"
          :actions="bottle.bottle_actions"
          class="mt-5"
      />
      <modal :visible="showModal" @close="showModal = false">
        <template v-slot:header>
          <h2>Flasche Nr. {{ bottle.serial }} bewegen</h2>
        </template>
        <template v-slot:default>
          <div class="row align-items-end">
            <div class="col-12 col-sm-6 col-lg-3">
              <data-input
                  label="Datum der Aktion"
                  type="date"
                  v-model="modalData.date"
              />
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <data-input
                  label="Uhrzeit der Aktion"
                  type="time"
                  v-model="modalData.time"
              />
            </div>
            <div class="col">
              <slot-based-select label="Wohin soll die Flasche bewegt werden?"
                                 class="pb-1"
                                 :options="bottleActions"
                                 v-model="modalData.action"
              >
                <template v-slot:default="{option}">
                  <option :value="option.action">{{ option.label }}</option>
                </template>
              </slot-based-select>
            </div>
          </div>


          <template v-if="modalData.action === 'entered_storage'">
            <label for="due_date" class="small">{{ $t('Ablaufdatum:') }}</label>
            <input type="date"
                   ref="dueDate"
                   id="due_date"
                   class="form-control"
                   required
                   :min="minDate"
                   :value="selectedDueDate || bottle.due.split('T').shift()"
                   @input="setSelectedDueDate($event.target.value)"
            />
          </template>
          <slot-based-select v-if="modalData.action === 'at_customer'"
                             label="Kunde"
                             :options="sortedCustomers"
                             v-model="modalData.customer_id"
          >
            <template v-slot:default="{option}">
              <option style="font-family: monospace" :value="option.id">
                {{ [option.customer_number, option.name || '[unbenannt]'].join(' / ') }}
              </option>
            </template>
          </slot-based-select>

          <slot-based-select v-else-if="modalData.action === 'from_customer'"
                             label="Fahrer"
                             :options="sortedDrivers"
                             v-model="modalData.user_id"
          >
            <template v-slot:default="{option}">
              <option :value="option.id">{{ option.name || option.username }}</option>
            </template>
          </slot-based-select>


        </template>
        <template v-slot:footer>
          <button :disabled="!modalData.action || (modalData.action === 'at_customer' && !modalData.customer_id) || (modalData.action === 'from_customer' && !modalData.user_id)"
                  class="btn btn-primary px-3  btn-sm"
                  @click="moveBottle"
          >
            Bewegen <i class="fas fa-dolly pl-4"></i>
          </button>
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

import SearchBar from "../../components/admin/SearchBar";
import Heading from "../../components/admin/Heading";
import BottleState from "../../components/admin/BottleState";
import timeFormatter from "../../mixins/timeFormatter";
import BottleActionLog from "../../components/admin/BottleActionLog";
import Modal from "../../components/admin/Modal";
import DataSelect from "../../components/DataSelect";
import SlotBasedSelect from "../../components/SlotBasedSelect";
import DataInput from "@/components/DataInput";


export default {
  name: "BottleDetail",
  components: {DataInput, SlotBasedSelect, DataSelect, Modal, BottleActionLog, BottleState, Heading, SearchBar},
  mixins: [timeFormatter],
  data() {
    return {
      // bottle: null,
      showModal: false,
      modalData: {
        date: dayjs().format("YYYY-MM-DD"),
        time: dayjs().format("HH:mm:ss"),
        action: '',
        customer_id: '',
        user_id: '',
      },
      minDate: new Date().toISOString().split('T').shift(),

    }
  },
  computed: {
    ...mapState({
      bottle: state => state.Api.Bottles.current,
      customers: state => state.Api.Customers.all,
      users: state => state.Api.Users.all,
      selectedDueDate: state => state.selectedDueDate
    }),
    ...mapGetters({
      getLatestActionId: "getLatestActionId"
    }),
    sortedDrivers() {
      return this.users.filter(el => el.role === 'driver').sort((a, b) => {
        if (a.name < b.name)
          return -1;
        if (a.name > b.name)
          return 1;
        return 0;
      })
    },
    sortedCustomers() {
      return this.customers.slice().sort((a, b) => {
        if (a.name < b.name)
          return -1;
        if (a.name > b.name)
          return 1;
        return 0;
      })
    },
    bottleActions() {
      return [
        {
          action: 'entered_storage',
          label: this.$t('Ins Lager (voll)')
        },
        {
          action: 'at_customer',
          label: this.$t('Zum Kunden')
        },
        {
          action: 'from_customer',
          label: this.$t('Ins Auto eines Fahrers')
        },
        {
          action: 'back_in_storage',
          label: this.$t('Ins Lager (leer)')
        },
        {
          action: 'left_storage',
          label: this.$t('Zurück zum Großhandel')
        },
      ]
    }
  },
  mounted() {
    this.fetchBottleDetails({id: this.$route.params.bottle_id})
        .catch(this.apiErrorHandler);

    this.refreshCustomers()
        .catch(this.apiErrorHandler);
    this.refreshUsers()
        .catch(this.apiErrorHandler);
  },
  methods: {
    ...mapActions({
      refreshCustomers: "Api/Customers/all",
      refreshUsers: "Api/Users/all",
      fetchBottleDetails: "Api/Bottles/get",
      addBottlesToInventory: "Api/Bottles/entered_storage",
      moveBottlesAtCustomer: "Api/Bottles/at_customer",
      moveBottlesFromCustomer: "Api/Bottles/from_customer",
      moveBottlesBackInStorage: "Api/Bottles/back_in_storage",
      bottlesLeftStorage: "Api/Bottles/left_storage"
    }),
    ...mapMutations({
      setSelectedDueDate: "setSelectedDueDate",
      setSelectedBottleType: "setSelectedBottleType",
      addOrUpdateBottle: "Api/Bottles/addOrUpdate",
      setCurrentBottle: "Api/Bottles/setCurrent"
    }),
    edit() {
      this.$router.push(['', 'admin', 'bottles', this.$route.params.bottle_id, 'edit'].join(('/'))).catch(() => null);
    }
    ,
    moveBottle() {
      let action = null;
      let [hour, minute, second] = this.modalData.time.split(':')
      let date = dayjs(this.modalData.date).hour(hour).minute(minute).second(second).local().format(this.$config.API_DATE_TIME_FORMAT)
      switch (this.modalData.action) {
        case 'entered_storage':
          this.setSelectedBottleType(this.bottle.type);
          action = this.addBottlesToInventory({
            bottles: [{
              ...this.bottle,
              date
            }]
          });
          break;
        case
        'at_customer':
          action = this.moveBottlesAtCustomer({
            bottles: [{
              serial: this.bottle.serial,
              customer_id: this.modalData.customer_id,
              date
            }]
          });
          break;
        case
        'from_customer':
          action = this.moveBottlesFromCustomer({
            bottles: [{
              serial: this.bottle.serial,
              driver_id: this.modalData.user_id,
              date
            }]
          });
          break;
        case
        'back_in_storage' :
          action = this.moveBottlesBackInStorage({
            bottles: [{
              serial: this.bottle.serial,
              date
            }]
          });
          break;
        case
        'left_storage' :
          action = this.bottlesLeftStorage({
            bottles: [{
              serial: this.bottle.serial,
              date
            }]
          });
          break;
      }

      if (action) {
        action
            .then(res => {
              this.showModal = false;
              this.fetchBottleDetails({id: this.$route.params.bottle_id})
                  .then(res => {
                    this.addOrUpdateBottle({item: res});
                    this.setCurrentBottle({item: res})
                  })
                  .catch(this.apiErrorHandler);
            })
            .catch(this.apiErrorHandler);
      }

    },
  }
}
</script>

<style scoped>

</style>
